import {
    getAxisData,
    getAggregation,
    getAggregationDate,
    getXAxisBreakdown,
} from 'lib/services/forceBI';
import { CHART_TYPES } from 'shards/ForceBI/constants';

const getIsReadOnly = ({ fieldId, data }) => {
    if (fieldId === 'yAxis' && !data?.entity) return true;
    if (fieldId === 'xAxis' && !data?.xAxisEntity) return true;
    if (fieldId === 'xAxisEntity' && !data?.entity) return true;
    if (fieldId === 'xAxisBreakdown' && !data?.xAxisBreakdownEntity) return true;
    if (fieldId === 'xAxisBreakdownEntity' && !data?.entity) return true;
    return false;
};

const getIsRequired = ({ fieldId, data }) => {
    if (fieldId === 'xAxisAggregationDate' && data?.xAxis && data?.xAxis?.isDateField) return true;
    return false;
};

const getIsHidden = ({ fieldId, data }) => {
    if (
        fieldId === 'xAxisBreakdown' &&
        (data?.chartType === CHART_TYPES.pie || data?.chartType === CHART_TYPES.doughnut)
    ) {
        return true;
    }
    return false;
};

const shouldRenderField = ({ fieldId, data }) => {
    if (fieldId === 'xAxisAggregationDate' && (!data?.xAxis || !data?.xAxis?.isDateField)) {
        return false;
    } else if (
        fieldId === 'xAxisBreakdownAggregationDate' &&
        (!data?.xAxisBreakdown || !data?.xAxisBreakdown?.isDateField)
    ) {
        return false;
    }
    return true;
};

export const afterOnChange = ({ fieldId, value, setOptions }) => {
    if (fieldId === 'entity' && value && setOptions) {
        Promise.all([getAxisData(value, 'y'), getAxisData(value, 'x')]).then(([yData, xData]) => {
            return setOptions({ y: yData || [], x: xData || [] });
        });
    }
};

const getAxisOptions = (axis) => (entity) => () => {
    return getAxisData(entity, axis);
};

const getXAxisBreakdownOptions = (entity) => () => {
    return getXAxisBreakdown(entity);
};

const getCustomAggregationOptions =
    ({ data }) =>
    () => {
        return new Promise((resolve, reject) => {
            getAggregation().then((options) => {
                const yAxisValue = data?.yAxis?.value || '';
                let isNumeric = false;
                let finalOptions = options;

                // Those yAxis options containing 'id' after '.' should be considered as numeric
                // and only have one yAxisAggregation method which is 'count'.
                if (yAxisValue && yAxisValue.split('.')[1] === 'id') isNumeric = true;

                if (isNumeric && options) {
                    finalOptions = options.filter((current) => current.value === 'count');
                }

                resolve(finalOptions);
            });
        });
    };

export const getCrudSchema = ({ t }) => [
    {
        title: t('label_info'),
        show: true,
        fields: [
            {
                id: 'chartType',
                type: 'chartSelector',
                label: t('label_chart'),
                isRequired: true,
            },
            {
                id: 'title',
                type: 'text',
                label: t('label_forcebi_crud_input_title'),
                placeholder: t('label_forcebi_crud_input_title_placeholder'),
                isRequired: true,
            },
            // for entity we use preloaded options to reduce time of loading options
            // is the only field where we can do that because the options do not depend on other fields selection
            {
                id: 'entity',
                type: 'singleValueList',
                label: t('label_forcebi_crud_input_entity'),
                hint: t('label_forcebi_crud_input_entity_description'),
                placeholder: t('label_forcebi_crud_placeholder_select_entity'),
                isRequired: true,
            },
            {
                id: 'yAxis',
                type: 'singleValueList',
                label: t('label_forcebi_crud_input_y_axis'),
                hint: t('label_forcebi_crud_input_y_axis_description'),
                placeholder: t('label_selectone'),
                placeholderReadOnly: t('label_disabled'),
                getIsReadOnly,
                isRequired: true,
                parentId: 'entity',
                loadOptions: getAxisOptions('y'),
                cacheOptions: false,
            },
            {
                id: 'xAxisGroup',
                type: 'selectWithEntity',
                label: t('label_forcebi_crud_input_x_axis'),
                parentId: 'entity',
                isRequired: true,
                subFields: [
                    // This first field is a field we don't have to send to backend it's just to make the CRUD more understandable for the user.
                    {
                        id: 'xAxisEntity',
                        placeholder: t('label_forcebi_crud_placeholder_select_entity'),
                        placeholderReadOnly: t('label_disabled'),
                        loadCustomOptions: getEntityOptions,
                        isRequired: true,
                        getIsReadOnly,
                    },
                    {
                        id: 'xAxis',
                        hint: t('label_forcebi_crud_input_x_axis_description'),
                        placeholder: t('label_selectone'),
                        placeholderReadOnly: t('label_disabled'),
                        getIsReadOnly,
                        isRequired: true,
                        parentId: 'entity',
                        loadOptions: getAxisOptions('x'),
                        cacheOptions: false,
                    },
                ],
            },
            {
                id: 'xAxisAggregationDate',
                type: 'singleValueList',
                label: t('label_aggregation_date'),
                hint: t('label_aggregation_date_description'),
                placeholder: t('label_selectone'),
                getIsReadOnly,
                getIsRequired,
                loadOptions: getAggregationDate,
                shouldRenderField: shouldRenderField,
            },
            {
                id: 'yAxisAggregation',
                type: 'singleValueList',
                label: t('label_aggregation_method'),
                hint: t('label_aggregation_method_description'),
                placeholder: t('label_selectone'),
                getIsReadOnly,
                isRequired: true,
                loadCustomOptions: getCustomAggregationOptions,
                cacheOptions: false,
            },
            {
                id: 'xAxisBreakdownGroup',
                type: 'selectWithEntity',
                label: t('label_x_axis_breakdown'),
                getIsHidden,
                subFields: [
                    {
                        id: 'xAxisBreakdownEntity',
                        placeholder: t('label_forcebi_crud_placeholder_select_entity'),
                        placeholderReadOnly: t('label_disabled'),
                        loadCustomOptions: getEntityOptions,
                        getIsReadOnly,
                    },
                    {
                        id: 'xAxisBreakdown',
                        hint: t('label_x_axis_breakdown_description'),
                        placeholder: t('label_selectone'),
                        placeholderReadOnly: t('label_disabled'),
                        getIsReadOnly,
                        parentId: 'entity',
                        loadOptions: getXAxisBreakdownOptions,
                        cacheOptions: false,
                    },
                ],
            },
            {
                id: 'xAxisBreakdownAggregationDate',
                type: 'singleValueList',
                label: t('label_x_axis_breakdown_aggregation_date'),
                hint: t('label_x_axis_breakdown_aggregation_date_description'),
                placeholder: t('label_selectone'),
                getIsReadOnly,
                getIsHidden,
                isRequired: false,
                loadOptions: getAggregationDate,
                shouldRenderField: shouldRenderField,
            },
        ],
    },
];

// We define the entity options here to have them accessible immediately and not having to wait for responses
// when matching entities from xAxis and xAxisBreakdown
export const getEntityOptions = ({ t }) => {
    return [
        { label: t('title_accounts'), value: 'empresas' },
        { label: t('title_contacts'), value: 'contactos' },
        { label: t('title_opportunities'), value: 'expedientes' },
        { label: t('title_activities'), value: 'activities' },
        { label: t('label_salesorders'), value: 'tblsalesorders' },
    ];
};

// Within sales orders options, we can find also productos and tblsalesorderlines, something similar happens with activities and gestiones.
// We need to show them in the dropdown when sales orders is selected
// We also need to map the related entities to the main entity
// This is used to show the related entities in the dropdown when the main entity is selected
export const getRelatedEntities = () => {
    return {
        tblsalesorders: ['tblsalesorderslines', 'productos'],
        activities: ['gestiones'],
    };
};

// This serves the same purpose as getRelatedEntities but in reverse. Helps to identify the correct entity for each related entity.
export const getRelatedEntitiesReverse = () => {
    return {
        tblsalesorderslines: 'tblsalesorders',
        productos: 'tblsalesorders',
        gestiones: 'activities',
    };
};
